import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { Calendar, type DateRangeValue, toDateRange } from 'src/components/ui/calendar';
import moment from 'moment/moment';

export interface PaymentHistoryDateFilterValues {
  date: DateRangeValue;
}

interface PaymentHistoryFilterPopupFormProps {
  onSubmit: (values: PaymentHistoryDateFilterValues) => void;
  dateDefault: DateRangeValue;
  // Reports whether the picked range has both ends, so the dialog can gate its submit button.
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

export const PaymentHistoryFilterPopupForm = ({
  onSubmit,
  dateDefault,
  onCanSubmitChange = () => {},
}: PaymentHistoryFilterPopupFormProps) => {
  const { watch, handleSubmit, control, setValue } = useForm<PaymentHistoryDateFilterValues>({
    defaultValues: {
      date: {
        from: '',
        to: '',
      },
    },
    resolver: formResolver(
      object({
        date: object({
          from: stringScheme({ required: false }).label('Date from'),
          to: stringScheme({ required: false }).label('Date to'),
        }),
      }),
    ),
  });

  const date = watch('date');
  const canSubmit = !!date?.from && !!date?.to;

  const submit = (formValues: PaymentHistoryDateFilterValues) => {
    onSubmit(formValues);
  };

  useEffect(() => {
    setValue('date', dateDefault);
  }, [dateDefault]);

  useEffect(() => {
    onCanSubmitChange(canSubmit);
  }, [canSubmit]);

  return (
    <form
      id="aply-date-filter-form"
      onSubmit={handleSubmit(submit)}
      className="flex flex-col gap-3 font-jamjuree"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-tiny leading-normal font-semibold uppercase tracking-[1px] text-ebony">
          Choose date range
        </p>
        <p className="text-xs leading-normal font-medium text-grey_450">
          {date?.from ? moment(date.from).format('ll') : 'Start date'} –{' '}
          {date?.to ? moment(date.to).format('ll') : 'End date'}
        </p>
      </div>
      <Controller
        control={control}
        name="date"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Calendar
            variant="filter"
            resetOnSelect
            mode="range"
            defaultMonth={
              value
                ? moment(toDateRange(value)?.to).subtract(1, 'months').toDate()
                : toDateRange(value)?.to
            }
            selected={toDateRange(value)}
            onSelect={onChange}
            numberOfMonths={2}
            disabled={{ after: new Date() }}
          />
        )}
      />
    </form>
  );
};
